import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useFacility } from "@/context/FacilityContext";
import { Button } from "@/components/ui/button";
import { Loader2, Link2, Check, X, UserCheck } from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "declined", label: "Declined" },
];

export default function ConnectionRequests() {
  const { has } = useFacility();
  const canDecide = has ? has("opd:edit") : false;
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    setRows(null);
    api.get(`/opd/connection-requests?status=${status}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [status]);
  useEffect(load, [load]);

  const decide = async (id, action) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/opd/connection-requests/${id}/${action}`);
      if (action === "approve") toast.success(data.matched ? "Approved — matched existing patient by mobile" : "Approved — new patient record created");
      else toast.success("Request declined");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  return (
    <div data-testid="connection-requests-page">
      <div className="flex items-center gap-2 mb-1"><Link2 className="h-5 w-5 text-blue-700" /><h1 className="font-display text-xl font-semibold text-slate-900">Connection Requests</h1></div>
      <p className="text-sm text-slate-500 mb-5">Patients on Mediva Health requesting to connect their records to this facility. Approving links their vault two-way.</p>

      <div className="flex gap-1.5 mb-4">
        {STATUSES.map((s) => (
          <button key={s.key} onClick={() => setStatus(s.key)} data-testid={`conn-filter-${s.key}`}
            className={`px-3.5 py-1.5 rounded-full text-sm ${status === s.key ? "bg-blue-700 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>{s.label}</button>
        ))}
      </div>

      {rows === null ? <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div> :
        rows.length === 0 ? <div className="text-center py-14 text-slate-400 border border-dashed border-slate-200 rounded-lg"><Link2 className="h-8 w-8 mx-auto mb-2 opacity-40" />No {status} requests.</div> : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="bg-white border border-slate-200 rounded-lg px-4 py-3 flex flex-wrap items-center gap-3" data-testid={`conn-req-${r.id}`}>
              <span className="h-9 w-9 rounded-full bg-blue-100 text-blue-700 grid place-items-center text-sm font-semibold">{(r.patient_name || "?").slice(0, 1).toUpperCase()}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800">{r.patient_name} <span className="text-xs text-slate-400">· {r.mobile || "no mobile"}{r.gender ? ` · ${r.gender}` : ""}</span></div>
                <div className="text-[11px] text-slate-400">Requested {new Date(r.created_at).toLocaleString()}{r.note ? ` · "${r.note}"` : ""}</div>
              </div>
              {r.status === "pending" && canDecide ? (
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => decide(r.id, "approve")} disabled={busy === r.id} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`approve-${r.id}`}>{busy === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1" /> Approve</>}</Button>
                  <Button size="sm" variant="outline" onClick={() => decide(r.id, "decline")} disabled={busy === r.id} className="text-red-600 border-red-200" data-testid={`decline-${r.id}`}><X className="h-4 w-4 mr-1" /> Decline</Button>
                </div>
              ) : (
                <span className={`text-xs font-medium rounded-full px-2.5 py-1 inline-flex items-center gap-1 ${r.status === "approved" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid={`conn-status-${r.id}`}>
                  {r.status === "approved" && <UserCheck className="h-3.5 w-3.5" />}{r.status}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
